import { useEffect, useState } from 'react';
import { Page, Table, PageTitleWithButton, Pagination } from '../../../components';
import { useSnackbar } from 'notistack';
import { Button as SMButton, Theme, Box, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Link, useHistory } from 'react-router-dom';
import { Add, Edit } from '@mui/icons-material';
import { getOptionsList } from '../../../fetch';
import { IOptionList } from '../../../models';
import { OptionFilters } from './option-filters';

export const ManageOptions = () => {
  const classes = useStyles();
  const history = useHistory();

  const { enqueueSnackbar } = useSnackbar();
  const [options, setOptions] = useState<IOptionList[]>([]);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [isLoadingOptions, setLoadingOptions] = useState<boolean>(true);
  const [nameSearch, setNameSearch] = useState<string | null>(null);
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [filters, setFilters] = useState<{
    name?: string;
    optionSet?: string;
    phase?: string;
  }>({});
  const [sortDirection, setSortDirection] = useState<{
    optionSet?: 'Asc' | 'Desc';
    name?: 'Asc' | 'Desc';
    phase?: 'Asc' | 'Desc';
  }>({
    optionSet: 'Asc'
  });
  const [selectedSort, setSelectedSort] = useState<string>('optionSet');
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);

  const fetchOptions = async () => {
    try {
      setLoadingOptions(true);
      const res = await getOptionsList({
        page: page + 1,
        perPage: perPage,
        name: nameSearch,
        optionSetId: selectedOptionId ? Number(selectedOptionId) : null,
        phaseId: selectedPhaseId ? Number(selectedPhaseId) : null,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string
      });
      setRecordCount(res.totalRecordCount);
      setOptions(res.records);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error loading Options, please refresh and try again.', {
        variant: 'error'
      });
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, selectedSort, sortDirection, filters]);

  const handleEditOption = option => {
    history.push(`/development/options/${option}/general-information`);
  };

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const columns = [
    {
      Header: 'Option Set',
      accessor: 'optionSetName',
      isServerSorted: selectedSort === 'optionSet',
      isServerSortedDesc: sortDirection.optionSet === 'Desc',
      handleClickColumn: () => handleClickColumn('optionSet')
    },
    {
      Header: 'Name',
      accessor: 'name',
      isServerSorted: selectedSort === 'name',
      isServerSortedDesc: sortDirection.name === 'Desc',
      handleClickColumn: () => handleClickColumn('name')
    },
    {
      Header: 'Phase',
      accessor: 'phaseName',
      isServerSorted: selectedSort === 'phase',
      isServerSortedDesc: sortDirection.phase === 'Desc',
      handleClickColumn: () => handleClickColumn('phase')
    },
    {
      id: 'actions',
      accessor: '',
      Header: '',
      sort: false,

      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IOptionList } };
      }) => {
        return (
          <div className={classes.actionButtons}>
            <SMButton
              id="edit-framer"
              type="button"
              color="primary"
              startIcon={<Edit />}
              onClick={() => handleEditOption(original.optionId)}
            >
              Details
            </SMButton>
          </div>
        );
      }
    }
  ];

  return (
    <Page title="Options">
      <PageTitleWithButton
        pageTitleProps={{
          title: 'Options'
        }}
        button={
          <Box display="flex">
            <Link className={classes.link} to={`/development/options/add/general-information`}>
              <Button
                id="add-option"
                startIcon={<Add />}
                color="primary"
                variant="contained"
                className={classes.addOptionButton}
              >
                Add Option
              </Button>
            </Link>
          </Box>
        }
      />
      <OptionFilters
        isLoading={isLoadingOptions}
        handleNameSearch={(clearSearch?: boolean) => {
          setPage(0);
          setFilters({
            ...filters,
            name: nameSearch
          });

          if (clearSearch) {
            setNameSearch('');
          }
        }}
        applyFilters={(clearFilters?: boolean) => {
          if (clearFilters) {
            setFilters({
              name: '',
              optionSet: '',
              phase: ''
            });
          } else {
            setPage(0);
            setFilters({
              ...filters
            });
          }
        }}
        setPage={setPage}
        hasAppliedFilters={hasAppliedFilters}
        setHasAppliedFilters={setHasAppliedFilters}
        searchName={nameSearch}
        setSearchName={setNameSearch}
        selectedPhaseId={selectedPhaseId}
        setSelectedPhaseId={setSelectedPhaseId}
        selectedOptionId={selectedOptionId}
        setSelectedOptionId={setSelectedOptionId}
      />
      <Table
        columns={columns}
        data={options}
        isLoading={isLoadingOptions}
        hidePagination
        mobileProps={{
          handleEdit: (option: any) => handleEditOption(options)
        }}
      />
      {!isLoadingOptions && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: '1rem'
  },
  link: {
    textDecoration: 'none'
  },
  addOptionButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  },
  actionButtons: {
    textAlign: 'right',
    display: 'flex'
  }
}));
